import { AppBar, Hidden, IconButton, Menu, MenuItem, Toolbar, Drawer, Grid, Box } from '@material-ui/core'
import Logo from '@assets/white-logo.png'
import React, { ReactNode, useState } from 'react'
import { AccountCircle, Menu as MenuIcon, Settings } from '@material-ui/icons'
import { useHistory } from 'react-router-dom'
import './layout.css'
import { useDispatch, useSelector } from 'react-redux'
import { clearLogin, selectIsLoading } from '@views/loginView/state/loginSlice'
import { userProfile, clearprofile } from '@views/profile/state/profileSlice'
import { Role, roleLabel } from '@types'
import TimeTrackerNavbarControl from '@officeTimeTracker/components/TimeTrackerNavbarControl'
import { OfficeTimeTrackerProvider, useOfficeTimeTracker } from '@officeTimeTracker/context/OfficeTimeTrackerContext'
// import banner2 from '@assets/banner2.jpg'
import './carousel.css'
import { clearTracking } from '@views/tracking/state/trackingSlice2'
import { executiveMetricsRouteForRole } from '@views/executiveMetrics/utils'
import { agentMetricsRouteForRole } from '@views/agentMetrics/utils'

interface Props {
  children: ReactNode
}

const EXECUTIVE_COMMISSIONS_CUTOFF = new Date('2026-05-01T05:00:00.000Z').getTime()

const hasExecutiveCommissions = (createdAt?: string) => {
  if (!createdAt) return false
  const createdAtTime = new Date(createdAt).getTime()
  return Number.isFinite(createdAtTime) && createdAtTime >= EXECUTIVE_COMMISSIONS_CUTOFF
}

export const getRoutesByRole = (role: Role, createdAt?: string) => {
  const baseRoutes = [
    { name: 'Seguimiento', route: '/track' },
    { name: 'Recursos', route: '/resources' }
  ]

  const routesByRole: Record<Role, { name: string; route: string }[]> = {
    [Role.admin]: [
      { name: 'Mi club', route: '/club' },
      ...baseRoutes,
      { name: 'Metricas', route: '/metricas' },
      { name: 'Registro', route: '/signup/referenciador' },
      { name: 'Cumplimiento', route: '/commissions' },
      { name: 'Comisiones ejecutivos', route: '/executive-commissions' },
      { name: 'Comisiones agentes', route: '/agent-commissions' },
      { name: 'Administrar', route: '/manage' },
      { name: 'Analistas', route: '/analyst-review' },
      { name: 'Configuraciones', route: '/configurations' }
    ],
    [Role.referenciador]: [
      { name: 'Mi club', route: '/club' },
      ...baseRoutes,
      { name: 'Referenciar a un amigo', route: '/signup/referenciador' },
      { name: 'Comisiones', route: '/commissions-referrals' },
      { name: 'Anticipos', route: '/prepayments' }
    ],
    [Role.asesor]: [
      { name: 'Mi club', route: '/club' },
      ...baseRoutes,
      { name: 'Registro', route: '/signup/referenciador' }
    ],
    [Role.ejecutivo]: [
      executiveMetricsRouteForRole(Role.ejecutivo)!,
      { name: 'Mi club', route: '/club' },
      ...baseRoutes,
      { name: 'Registro', route: '/signup/referenciador' },
      { name: 'Casos', route: '/cases' },
      ...(hasExecutiveCommissions(createdAt) ? [{ name: 'Comisiones', route: '/executive-commissions' }] : [])
    ],
    [Role.agente]: [
      ...baseRoutes,
      { name: 'Registro', route: '/signup/referenciador' },
      { name: 'Mis comisiones', route: '/agent-commissions' },
      agentMetricsRouteForRole(Role.agente)!
    ],
    [Role.analista]: [
      { name: 'Seguimiento', route: '/analyst-track' },
      { name: 'Rechazados', route: '/analyst-rejected' }
    ],
    [Role.operaciones]: baseRoutes,
    [Role.gestor]: baseRoutes,
    [Role.casos]: baseRoutes,
    [Role.gerente]: [...baseRoutes, { name: 'Cumplimiento', route: '/commissions' }],
    [Role.procesos]: [
      { name: 'Comisiones ejecutivos', route: '/executive-commissions' },
      { name: 'Comisiones agentes', route: '/agent-commissions' }
    ]
  }

  // roles and their routes:
  // - Asesor: Mi club, Seguimiento, Recursos, Registro
  // - Admin: Mi club, Seguimiento, Recursos, Registro, Configuraciones, Cumplimiento, Administrar, Analistas
  // - Ejecutivo: Mi club, Seguimiento, Recursos, Registro, Casos
  // - Agente: Seguimiento, Recursos, Registro
  // - Referenciador: Mi club, Seguimiento, Recursos, Referenciar a un amigo, Comisiones, Anticipos
  // - Analista: Seguimiento

  return routesByRole[role] || baseRoutes
}

const Routes: React.FC = () => {
  const { role, createdAt } = useSelector(userProfile)

  const routes = getRoutesByRole(role, createdAt)
  const history = useHistory()
  return (
    <>
      {routes.map((r, i) => (
        <Box key={i} margin="auto 10px">
          {r.route === '/configurations' ? (
            <IconButton
              aria-label={r.name}
              title={r.name}
              color="inherit"
              onClick={() => {
                history.push(r.route)
              }}
            >
              <Settings />
            </IconButton>
          ) : (
            <div
              className="buttons_tab"
              onClick={() => {
                history.push(r.route)
              }}
              key={r.name}
            >
              {r.name}
            </div>
          )}
        </Box>
      ))}
    </>
  )
}

const LayoutContent: React.FC<Props> = ({ children }: Props) => {
  const { role } = useSelector(userProfile)
  const dispatch = useDispatch()
  const { session, logoutAndStop } = useOfficeTimeTracker()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openMobile, setOpenMobile] = useState(false)
  const open = Boolean(anchorEl)
  const { isLogged } = useSelector(selectIsLoading)
  const { name, email } = useSelector(userProfile)
  const history = useHistory()
  const isShowRole = email !== 'asesoria@cocapitalizaciones.com'
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const clearLocalSession = () => {
    setAnchorEl(null)
    dispatch(clearTracking())
    dispatch(clearLogin())
    dispatch(clearprofile())
  }

  const logout = async () => {
    if (session && ['running', 'paused'].includes(session.status)) {
      const confirmed = window.confirm(
        'Si cierras sesion, tu registro de tiempo se detendra y no podras registrar mas horas durante el dia de hoy. ¿Deseas continuar?'
      )

      if (!confirmed) {
        setAnchorEl(null)
        return
      }

      try {
        await logoutAndStop()
        clearLocalSession()
        return
      } catch (error) {
        window.alert('No se pudo cerrar la jornada antes de salir. Tu sesion seguira abierta.')
        setAnchorEl(null)
        return
      }
    }

    clearLocalSession()
  }

  const handleClose = (route?: string) => {
    setAnchorEl(null)
    if (route) history.push(route)
  }

  return (
    <>
      {isLogged ? (
        <AppBar position="fixed" className="center_content">
          <Toolbar>
            <Box className="layout-navbar">
              <Box className="layout-navbar-left">
                <Hidden mdUp>
                  <Grid item xs={1}>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="menu"
                      aria-controls="menu-mobile"
                      onClick={() => {
                        setOpenMobile(true)
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                    <Drawer
                      anchor="left"
                      open={openMobile}
                      onClose={() => {
                        setOpenMobile(false)
                      }}
                    >
                      <div
                        onClick={() => {
                          setOpenMobile(false)
                        }}
                        className="buttons buttons_mobile"
                      >
                        <Routes />
                      </div>
                    </Drawer>
                  </Grid>
                </Hidden>
                <div
                  className="navbar_title"
                  onClick={() => {
                    history.push('/')
                  }}
                >
                  <img src={Logo} className="navbar_logo" />
                </div>
              </Box>
              <Hidden smDown>
                <Box display="flex" flexDirection="row">
                  <Routes />
                </Box>
              </Hidden>

              <Box className="layout-navbar-right">
                <Box className="layout-time-tracker-slot">
                  <TimeTrackerNavbarControl
                    onClick={() => {
                      history.push('/registro-horas')
                    }}
                  />
                </Box>
                <Box>
                  <div className="profile_section">
                    <Box className="profile_info">
                      <Box>{name}</Box>
                      {isShowRole ? <Box>{roleLabel[role]}</Box> : <></>}
                    </Box>
                    <div>
                      <IconButton
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleMenu}
                        color="inherit"
                      >
                        <AccountCircle />
                      </IconButton>
                      <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                        keepMounted
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                        open={open}
                        onClose={() => handleClose()}
                      >
                        <MenuItem onClick={() => handleClose('profile')}>Perfil</MenuItem>
                        <MenuItem onClick={() => void logout()}>Cerrar sesion</MenuItem>
                      </Menu>
                    </div>
                  </div>
                </Box>
              </Box>
            </Box>
          </Toolbar>
          {/* <Dialog
            fullWidth
            maxWidth="lg"
            open={openFlayer2}
            onClose={() => setOpenFlayer2(false)}
          >
            <DialogTitle id="alert-dialog-title">¡importante!</DialogTitle>
            <DialogContent>
              <img className="bannerImg" src={banner2} />
            </DialogContent>
          </Dialog> */}
        </AppBar>
      ) : null}
      <Box>{children}</Box>
      {process.env.REACT_APP_ENV === 'dev' ? <div className="dev-sign">{process.env.REACT_APP_ENV}</div> : <></>}
    </>
  )
}

const Layout: React.FC<Props> = ({ children }: Props) => (
  <OfficeTimeTrackerProvider>
    <LayoutContent>{children}</LayoutContent>
  </OfficeTimeTrackerProvider>
)

export default Layout
